import {devAssert} from "@buildwithflux/core";
import {areWeInStorybook, areWeTestingWithJest, hasBrowserWindowAccess, isDevEnv} from "@buildwithflux/shared";
import {get, set} from "lodash";

import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {type FluxServices} from "./container";
import {createServiceContainer} from "./creator";

/**
 * A singleton instance that can be statically accessed with getActiveServicesContainer()
 */
let servicesSingleton: FluxServices | undefined = undefined;

/**
 * Do not add references to this function from the middle of application code. It is only for use from composition roots.
 */
export function getActiveServicesContainerSafelyFromRoot(): FluxServices {
    return getActiveServicesContainerInner(true);
}

/**
 * Get the currently active services container singleton.
 *
 * @deprecated Do not add new references to this function, because it represents the Service Locator anti-pattern. Do
 *             injection instead. See https://coda.io/d/_dZhtNCBApM2/Why-not-Service-Locator_susQP
 */
export function getActiveServicesContainerBadlyAsServiceLocator(): FluxServices {
    return getActiveServicesContainerInner(false);
}

function getActiveServicesContainerInner(fromRoot = false): FluxServices {
    /*
     * We definitely don't want this code path: even if we need a static
     * reference to the active service container, we want the composition root
     * to simply define the container, not rely on lazy instantiation of the
     * container itself. (i.e. there should always be a container, so we
     * shouldn't need to create one here)
     */
    if (typeof servicesSingleton === "undefined") {
        const container = createServiceContainer();

        if ((isDevEnv() || areWeTestingWithJest()) && !fromRoot) {
            // It's normal to get this warning, but if you want to help push the state of the art forward,
            // follow the stack trace, and remove whatever reference is statically creating the container early.
            // Known: store.ts
            container.logger.warn(
                "Creating container on first use: composition root should define container instance instead",
                new Error("Container created here"),
            );
        }

        setActiveServicesContainer(container);
    }

    if (!servicesSingleton) {
        // Will never happen, just to assert to TS that setActiveServicesContainer above has set the value
        throw new Error("No services singleton was created on static active service container miss");
    }

    return servicesSingleton;
}

let lastError: Error | undefined = undefined;

/**
 * Sets the active services container instance
 *
 * It's important that we can mutate this static global singleton, because we should set this state in the composition
 * root when possible (rather than leave it to be lazily instantiated via getActiveServicesContainer())
 */
export function setActiveServicesContainer(services: FluxServices): void {
    devAssert(
        typeof servicesSingleton === "undefined",
        "Existing service container is being replaced with a new instance - this can cause bad service references",
        FluxLogger,
        lastError,
    );

    if (isDevEnv()) {
        lastError = new Error("Previous container instantiation was here");
    }

    servicesSingleton = services;

    if (hasBrowserWindowAccess()) {
        // Load bearing for the Visual Tests, because they use this access to control the page under test
        set(window, ["__FLUX__", "services"], servicesSingleton);
    }
}

/**
 * Dispose effect services on a container if they were instantiated.
 * Uses Object.hasOwn to check without triggering lazy initialization.
 */
function disposeEffectServicesIfInstantiated(services: FluxServices): void {
    // Dispose effect services to cancel any pending RxJS timers (debounce, buffer, etc.)
    // This prevents "Firebase client already terminated" errors during test teardown
    // We use Object.hasOwn to check if the service was actually instantiated,
    // avoiding lazy initialization which could cause issues during shutdown
    if (Object.hasOwn(services, "partNotificationService")) {
        services.partNotificationService.dispose();
    }
    if (Object.hasOwn(services, "documentConfigService")) {
        services.documentConfigService.dispose();
    }
    if (Object.hasOwn(services, "documentSaveService")) {
        services.documentSaveService.dispose();
    }
    if (Object.hasOwn(services, "actionRecordService")) {
        services.actionRecordService.dispose();
    }
}

/**
 * Unsets a given services container as the active services container singleton, if it is (still) the active service container
 */
export function unsetActiveServicesContainer(services: FluxServices): void {
    if (servicesSingleton === services) {
        // Dispose effect services before clearing the singleton to cancel any pending timers
        disposeEffectServicesIfInstantiated(services);
        servicesSingleton = undefined;
    }

    if (hasBrowserWindowAccess() && get(window, ["__FLUX__", "services"]) === services) {
        set(window, ["__FLUX__", "services"], undefined);
    }
}

/**
 * Less preferred method of clearning an active services container singleton (prefer unsetActiveServicesContainer)
 *
 * But necessary in testing cases where we want to destroy an early-initialized singleton without initializing one ourselves
 *
 * Should not be used outside of testing contexts
 */
export function unsetAnyActiveServicesContainer(): void {
    devAssert(
        areWeTestingWithJest() || areWeInStorybook(),
        "unsetAnyActiveServicesContainer should only be used in testing contexts",
        FluxLogger,
    );

    if (servicesSingleton) {
        // Dispose effect services before clearing the singleton to cancel any pending timers
        disposeEffectServicesIfInstantiated(servicesSingleton);
        servicesSingleton = undefined;
    }

    if (hasBrowserWindowAccess()) {
        set(window, ["__FLUX__", "services"], undefined);
    }
}
